import { memo, useMemo } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DotsThree } from "@/components/ui/icons";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { WorkspaceIcon } from "@/components/ui/workspace-icon";
import { DMRowItem } from "./DMRowItem";
import type { DmRow as DmRowType, WorkspaceInfo } from "./dm-display-filters";
import type { DmActions } from "./use-dm-actions";

interface DmRowProps {
  dm: DmRowType;
  isSelected: boolean;
  isUnread: boolean;
  isCollaborator: boolean;
  actions: DmActions;
  /** Home view only — passes the viewer's workspaces so DMRowItem can
   *  render the shared-workspaces tooltip. Workspace scope passes
   *  undefined to suppress the tooltip. */
  myWorkspaces?: WorkspaceInfo[];
  inviteBadge: string | null;
  inviteMethod: "email" | "link" | null;
}

/**
 * Wraps `DMRowItem` with the hover-revealed conversation actions menu.
 * Pulled out of `DMList` so the parent is a pure orchestrator over its sections.
 *
 * Wrapped in `memo()` + consumes `actions: DmActions` (stable ref) so
 * identity-stable props let `DmRow.memo` bail out cleanly on Sidebar
 * re-renders.
 */
export const DmRow = memo(function DmRow({
  dm,
  isSelected,
  isUnread,
  isCollaborator,
  actions,
  myWorkspaces,
  inviteBadge,
  inviteMethod,
}: DmRowProps) {
  const [otherMembershipsRaw] = useQuery(
    myWorkspaces && dm.userId
      ? queries.workspace_members.forUser(dm.userId)
      : null,
  );
  const sharedWorkspaces = useMemo(() => {
    if (!myWorkspaces || !otherMembershipsRaw) return [];
    const otherWsIds = new Set(otherMembershipsRaw.map((m) => m.workspace_id));
    return myWorkspaces.filter((ws) => otherWsIds.has(ws.id));
  }, [myWorkspaces, otherMembershipsRaw]);

  const row = (
    <DMRowItem
      dm={dm}
      isSelected={isSelected}
      isUnread={isUnread}
      isCollaborator={isCollaborator}
      actions={actions}
      inviteBadge={inviteBadge}
      inviteMethod={inviteMethod}
    />
  );

  return (
    <div className="group/dm relative">
      {sharedWorkspaces.length > 0 ? (
        <TooltipProvider delayDuration={400}>
          <Tooltip>
            <TooltipTrigger asChild>{row}</TooltipTrigger>
            <TooltipContent side="right" className="max-w-52">
              <p className="text-[10px] font-medium text-primary-foreground/70 mb-1">
                Shared workspaces
              </p>
              <div className="space-y-1">
                {sharedWorkspaces.map((ws) => (
                  <div key={ws.id} className="flex items-center gap-1.5">
                    <WorkspaceIcon
                      name={ws.name}
                      logoUrl={ws.logo_url}
                      slug={ws.slug}
                      size="sm"
                    />
                    <span className="text-xs truncate">{ws.name}</span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        row
      )}
      <div className="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 items-center group-hover/dm:flex group-focus-within/dm:flex has-[[data-state=open]]:flex">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <ToolbarIconButton
              className="data-[state=open]:bg-foreground/10"
              title="Conversation actions"
              aria-label={`Actions for ${dm.displayName}`}
            >
              <DotsThree size={14} className="text-muted-foreground" />
            </ToolbarIconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => actions.hideChannel(dm.channelId)}
              className="cursor-pointer"
            >
              Hide conversation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
});
